<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, useId } from 'vue'
import { ChevronDown } from '@lucide/vue'

const props = withDefaults(
  defineProps<{
    label: string
    width?: number
    iconOnly?: boolean
    buttonClass?: string
    role?: 'dialog' | 'menu' | 'listbox'
    triggerRole?: 'combobox'
    disabled?: boolean
  }>(),
  { width: 240, role: 'menu', buttonClass: 'btn-quiet gap-2', triggerRole: undefined },
)
const id = useId()
const open = ref(false)
const trigger = ref<HTMLButtonElement | null>(null)
const panel = ref<HTMLElement | null>(null)
const teleportTarget = ref<HTMLElement>(document.body)
const position = ref({ top: '0px', left: '0px', width: `${props.width}px`, maxHeight: '400px' })

function place(): void {
  if (!trigger.value || !open.value) return
  const rect = trigger.value.getBoundingClientRect()
  const width = Math.min(props.width, window.innerWidth - 24)
  const availableBelow = window.innerHeight - rect.bottom - 20
  const availableAbove = rect.top - 20
  const upwards =
    availableBelow < Math.min(panel.value?.scrollHeight || 220, 320) &&
    availableAbove > availableBelow
  const height = Math.min(
    panel.value?.scrollHeight || 320,
    upwards ? availableAbove : availableBelow,
  )
  position.value = {
    left: `${Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12))}px`,
    top: `${upwards ? Math.max(12, rect.top - height - 8) : rect.bottom + 8}px`,
    width: `${width}px`,
    maxHeight: `${Math.max(80, upwards ? availableAbove : availableBelow)}px`,
  }
}
function close(restoreFocus = true): void {
  open.value = false
  document.removeEventListener('pointerdown', outside)
  window.removeEventListener('resize', place)
  window.removeEventListener('scroll', place, true)
  if (restoreFocus) trigger.value?.focus()
}
function outside(event: PointerEvent): void {
  const target = event.target as Element | null
  if (
    !trigger.value?.contains(target) &&
    !panel.value?.contains(target) &&
    !target?.closest('[data-popup]')
  )
    close(false)
}
async function show(): Promise<void> {
  if (props.disabled) return
  teleportTarget.value = trigger.value?.closest<HTMLDialogElement>('dialog[open]') ?? document.body
  open.value = true
  await nextTick()
  place()
  document.addEventListener('pointerdown', outside)
  window.addEventListener('resize', place)
  window.addEventListener('scroll', place, true)
  const selected = panel.value?.querySelector<HTMLElement>('[aria-selected="true"]')
  const first = panel.value?.querySelector<HTMLElement>(
    'input:not([type="hidden"]),[role="menuitem"],[role="option"],button,a[href]',
  )
  ;(selected || first || panel.value)?.focus()
}
function keydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    close()
    return
  }
  if (
    !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) ||
    (event.target as HTMLElement).tagName === 'INPUT'
  )
    return
  const items = [
    ...(panel.value?.querySelectorAll<HTMLElement>('[role="menuitem"],[role="option"]') || []),
  ]
  if (!items.length) return
  event.preventDefault()
  const index = items.indexOf(document.activeElement as HTMLElement)
  const target =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? items.length - 1
        : (index + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length
  items[target]?.focus()
}
onBeforeUnmount(() => close(false))
</script>

<template>
  <button
    ref="trigger"
    type="button"
    :class="buttonClass"
    :disabled="disabled"
    :role="triggerRole"
    :aria-label="label"
    :title="iconOnly ? label : undefined"
    :aria-haspopup="role"
    :aria-expanded="open"
    :aria-controls="id"
    @click="open ? close() : show()"
    @keydown.down.prevent="show"
    @keydown.up.prevent="show"
    @keydown.esc.stop.prevent="close()"
  >
    <slot name="trigger"
      ><span>{{ label }}</span></slot
    >
    <ChevronDown
      v-if="!iconOnly"
      class="size-3.5 shrink-0 text-slate-400 transition"
      :class="open ? 'rotate-180' : ''"
      aria-hidden="true"
    />
  </button>
  <Teleport :to="teleportTarget">
    <div
      v-if="open"
      :id="id"
      ref="panel"
      :role="role"
      :aria-label="label"
      tabindex="-1"
      data-popup
      class="popup fixed z-[100] overflow-y-auto p-1.5"
      :style="position"
      @keydown="keydown"
    >
      <slot :close="close" />
    </div>
  </Teleport>
</template>
